import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../context/authcontext";
import CustomFieldSection from "../CustomFieldSection";
import FieldRenderer from "./FieldRenderer";
import SubmitButtonWithTooltip from "./SubmitButtonWithTooltip";
import TagsField from "./TagsField";
import { useTagsField } from "./useTagsField";
import AttachmentsField from "./AttachmentsField";
import { useAttachmentsField } from "./useAttachmentsField";
import ImageUploadField from "./ImageUploadField";
import { useCustomFieldsController } from "./useCustomFieldsController";
import {
  validateFields,
  getMissingRequiredLabels,
} from "./useEntityFormValidation";
import { FormCtx, UniversalFieldConfig, UniversalFormConfig } from "./types";

interface UniversalFormProps<T extends Record<string, any>> {
  config: UniversalFormConfig<T>;
}

function UniversalForm<T extends Record<string, any>>({
  config,
}: UniversalFormProps<T>) {
  const {
    formId,
    mode,
    record = null,
    resetKey,
    tenantId = null,
    initialValues,
    sections,
    customFields,
    tags,
    attachments,
    imageUpload,
    isDirty,
    buildSubmitPayload,
    onSubmit,
    onSuccess,
    onCancel,
    submitLabel,
    submittingLabel,
    cancelLabel,
    loading = false,
    loadingText = "Loading...",
    loadingContent,
    isSubmitting = false,
  } = config;

  const productsServicesField = useMemo(() => {
    for (const section of sections) {
      const field = section.fields.find((f) => f.type === "products-services");
      if (field) return field;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  const { currencyCode: defaultCurrencyCode } = useContext(AuthContext);
  const appliedCurrencyRef = useRef<string | null>(null);

  const currencyFieldNames = useMemo(
    () =>
      sections.flatMap((s) =>
        s.fields.filter((f) => f.type === "currency").map((f) => f.name)
      ),
    [sections]
  );

  const [values, setValuesState] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [productsServicesState, setProductsServicesState] = useState<any[]>(
    () => productsServicesField?.productsServices?.seedItems || []
  );
  const [hasDiscountErrors, setHasDiscountErrors] = useState(false);

  const tagsField = useTagsField(tags?.initial, {
    debounceMs: tags?.debounceMs,
  });
  const attachmentsField = useAttachmentsField(
    attachments?.existingAttachments,
    {
      maxFiles: attachments?.maxFiles,
      maxFileSizeMB: attachments?.maxFileSizeMB,
      allowedMimeTypes: attachments?.allowedMimeTypes,
    }
  );
  const customFieldsController = useCustomFieldsController(
    tenantId,
    customFields?.entityKey || "",
    record,
    mode
  );

  useEffect(() => {
    setValuesState(initialValues);
    setErrors({});
    setImageFile(null);
    setProductsServicesState(
      productsServicesField?.productsServices?.seedItems || []
    );
    setHasDiscountErrors(false);
    if (tags?.enabled) tagsField.resetTo(tags?.initial);
    if (attachments?.enabled) attachmentsField.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    if (mode !== "create" || !defaultCurrencyCode || currencyFieldNames.length === 0) return;
    const previousDefault = appliedCurrencyRef.current;
    appliedCurrencyRef.current = defaultCurrencyCode;
    setValuesState((prev) => {
      const prevRecord = prev as Record<string, any>;
      const patch: Record<string, any> = {};
      currencyFieldNames.forEach((name) => {
        const current = prevRecord[name];
        if (!current || (current === previousDefault && current !== defaultCurrencyCode)) {
          patch[name] = defaultCurrencyCode;
        }
      });
      return Object.keys(patch).length ? { ...prev, ...patch } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCurrencyCode, currencyFieldNames, resetKey, mode]);

  const setValue = (name: string, value: any) => {
    setValuesState((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const setValuesPatch = (patch: Partial<T>) => {
    setValuesState((prev) => ({ ...prev, ...patch }));
  };

  const getValue = (name: string) => (values as Record<string, any>)[name];

  const ctx: FormCtx<T> = useMemo(
    () => ({
      values,
      errors,
      mode,
      record,
      setValue,
      setValues: setValuesPatch,
      getValue,
      tags: tagsField.tags,
      setTags: tagsField.setTags,
      getTagsDiff: tagsField.getDiff,
      customFieldValues: customFieldsController.customFieldValues,
      customFieldErrors: customFieldsController.customFieldErrors,
      hasCustomFieldChanges: customFieldsController.hasChanges(),
      getCustomFieldsPayload: customFieldsController.buildSubmissionPayload,
      attachments: attachmentsField.files,
      existingAttachments: attachmentsField.existingAttachments,
      attachmentsToDelete: attachmentsField.attachmentsToDelete,
      imageFile,
      setImageFile,
      productsServices: productsServicesState,
      setProductsServices: setProductsServicesState,
      hasDiscountErrors,
      setHasDiscountErrors,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      values,
      errors,
      mode,
      record,
      tagsField.tags,
      customFieldsController.customFieldValues,
      customFieldsController.customFieldErrors,
      attachmentsField.files,
      attachmentsField.existingAttachments,
      attachmentsField.attachmentsToDelete,
      imageFile,
      productsServicesState,
      hasDiscountErrors,
    ]
  );

  const flatFields: UniversalFieldConfig<T>[] = useMemo(
    () => sections.flatMap((s) => s.fields),
    [sections]
  );

  const coreValidation = validateFields(flatFields, ctx);
  const coreMissingLabels = getMissingRequiredLabels(flatFields, ctx);

  const attachmentsBlocking = !!(attachments?.enabled && attachmentsField.error);
  const customFieldsMissingLabels = customFields
    ? customFieldsController.getMissingLabels()
    : [];

  const isFormValid =
    coreValidation.valid &&
    !attachmentsBlocking &&
    customFieldsMissingLabels.length === 0;
  const missingLabels = [...coreMissingLabels, ...customFieldsMissingLabels];

  const dirty = isDirty ? isDirty(ctx) : true;
  const disabledForNoChanges = mode === "edit" && !dirty;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateFields(flatFields, ctx);
    let nextErrors = validation.errors;
    let valid = validation.valid;

    if (attachmentsBlocking) {
      nextErrors = { ...nextErrors, attachments: attachmentsField.error };
      valid = false;
    }

    setErrors(nextErrors);
    if (!valid) return;

    if (customFields) {
      const cfValidation = customFieldsController.validateRequired();
      if (!cfValidation.valid) return;
    }

    if (hasDiscountErrors) return;

    if (mode === "edit" && isDirty && !isDirty(ctx)) return;

    try {
      const payload = await buildSubmitPayload(ctx, mode);
      const result = await onSubmit(payload);
      onSuccess?.(mode, result, ctx);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    if (loadingContent) return <>{loadingContent}</>;
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">{loadingText}</p>
      </div>
    );
  }

  return (
    <form id={formId} onSubmit={handleSubmit} noValidate>
      <style>{`
        #${formId} .is-invalid .react-select__control,
        #${formId} .react-select__control.is-invalid {
          border-color: #dc3545 !important;
        }
      `}</style>

      {imageUpload?.enabled && (
        <ImageUploadField
          imageFile={imageFile}
          onImageFileChange={setImageFile}
          existingImageUrl={imageUpload.existingImageUrl}
          placeholderImageUrl={imageUpload.placeholderImageUrl || ""}
          label={imageUpload.label}
          accept={imageUpload.accept}
          autoDefaultFileUrl={imageUpload.autoDefaultFileUrl}
          mode={mode}
          onAutoDefaultFileResolved={setImageFile}
        />
      )}

      {sections.map((section, idx) => {
        const visible =
          typeof section.visible === "function"
            ? section.visible(ctx)
            : section.visible !== false;
        if (!visible) return null;
        return (
          <div
            key={section.key || section.title || idx}
            className={section.className}
          >
            {section.title && (
              <h6 className="pb-2 mb-2 border-bottom">{section.title}</h6>
            )}
            <div className="row">
              {section.fields.map((field) => (
                <FieldRenderer key={field.name} field={field} ctx={ctx} />
              ))}
            </div>
          </div>
        );
      })}

      {tags?.enabled && <TagsField tagsField={tagsField} label={tags.label} />}

      {attachments?.enabled && (
        <AttachmentsField
          attachmentsField={attachmentsField}
          label={attachments.label}
        />
      )}

      {customFields && (
        <CustomFieldSection
          customFields={customFieldsController.customFields}
          customFieldValues={customFieldsController.customFieldValues}
          customFieldErrors={customFieldsController.customFieldErrors}
          onCustomFieldChange={customFieldsController.handleChange}
          onLoadOptions={customFieldsController.fetchFieldOptions}
          onLoadDependencyLevels={customFieldsController.fetchDependencyLevels}
          loading={customFieldsController.loading}
          error={customFieldsController.error}
          entityName={customFields.entityKey}
        />
      )}

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {cancelLabel || "Cancel"}
        </button>
        <SubmitButtonWithTooltip
          isValid={isFormValid}
          missingLabels={missingLabels}
          isSubmitting={isSubmitting}
          disabled={disabledForNoChanges}
          disabledReason="No Changes"
          submitLabel={submitLabel || (mode === "create" ? "Create" : "Update")}
          submittingLabel={submittingLabel}
          formId={formId}
        />
      </div>
    </form>
  );
}

export default UniversalForm;
